class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this)
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (
      document.documentElement.style.getPropertyValue(
        "--header-bottom-position-desktop"
      ) !== ""
    )
      return;
    document.documentElement.style.setProperty(
      "--header-bottom-position-desktop",
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define("header-menu", HeaderMenu);

class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector(".moonbase-header");
    this.headerIsAlwaysSticky =
      this.getAttribute("data-sticky-type") === "always" ||
      this.getAttribute("data-sticky-type") === "reduce-logo-size";
    this.headerBounds = {};
    this.lastScrollTop = 0;
    this.closeSearchSectionButton = this.querySelectorAll(
      ".search-form__close-button"
    );
    this.siteOverlay = document.querySelector(".site-overlay");

    this.closeSearchSectionButton.forEach((button) => {
      button.addEventListener("click", () => {
        document.body.classList.remove("overlay--search-active");
        document.body.classList.remove("search-open");
      });
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" || event.key === "Esc") {
        document.body.classList.remove("overlay--search-active");
        document.body.classList.remove("search-open");
      }
    });

    this.menuTopLevelItems = this.header.querySelectorAll(
      ".header__desktop-menu-item"
    );

    this.mobileSearchIcon = this.querySelector(".header__mobile-search-icon");
    this.mobileSearchCloseIcon = this.querySelector(
      ".header__mobile-search-close-icon"
    );

    this.mobileSearchCloseIcon.addEventListener("click", () => {
      this.setHeaderHeight();
      document.body.classList.remove("search-open");
      document.body.classList.remove("overlay--search-active");
    });

    this.siteOverlay.addEventListener("click", () => {
      this.setHeaderHeight();
      document.body.classList.remove("search-open");
      document.body.classList.remove("overlay--search-active");
    });

    this.mobileSearchIcon.addEventListener("click", () => {
      this.setHeaderHeight();
      document.body.classList.toggle("search-open");
    });

    this.menuTopLevelItems.forEach((item) => {
      item.addEventListener("mouseenter", this.onHover.bind(this));
      item.addEventListener("mouseleave", this.onHoverOut.bind(this));
    });

    setTimeout(() => {
      this.setHeaderHeight();
    }, 1000);

    window
      .matchMedia("(max-width: 990px)")
      .addEventListener("change", this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add("shopify-section-header-sticky");
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector("predictive-search");

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
    window.addEventListener("scroll", this.onScrollHandler, false);

    this.createObserver();
  }

  onHover(event) {
    const e = event;
    const megaMenu = e.target.querySelector(".mega-menu");
    if (megaMenu) {
      this.hoverTimeout = setTimeout(() => {
        megaMenu.open = true;
      }, 300);
    }
  }

  onHoverOut(event) {
    const e = event;
    const megaMenu = e.target.querySelector(".mega-menu");
    if (megaMenu) {
      clearTimeout(this.hoverTimeout);
      megaMenu.open = false;
    }
  }

  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  setHeaderHeight() {
    const announcementBar = document.querySelector(
      ".moonbase-announcement-bar"
    );
    let headerHeight = this.header.offsetHeight;

    if (
      window.innerWidth > 1024 &&
      announcementBar &&
      this.isInViewport(announcementBar)
    ) {
      headerHeight += announcementBar.offsetHeight;
    } else {
      headerHeight = this.header.offsetHeight;
    }

    document.documentElement.style.setProperty(
      "--header-height",
      `${headerHeight}px`
    );

    document.documentElement.style.setProperty(
      "--extended-header-height",
      `${headerHeight + 20}px`
    );
  }

  disconnectedCallback() {
    this.removeEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
    window.removeEventListener("scroll", this.onScrollHandler);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > 38) {
      document.body.classList.add("scrolled-past-announcement");
    } else {
      document.body.classList.remove("scrolled-past-announcement");
    }

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (
      scrollTop > this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      this.header.classList.add("scrolled-past-header");
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (
      scrollTop < this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      this.setHeaderHeight();
      this.header.classList.add("scrolled-past-header");
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.setHeaderHeight();
      this.header.classList.remove("scrolled-past-header");
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;

    // New scroll behavior
    const newscrollTop = window.scrollY || document.documentElement.scrollTop;
    if (newscrollTop > this.lastScrollTop && scrollTop > 400) {
      document.body.classList.add("scrolled-down");
      document.body.classList.remove("scrolled-up");
      this.setHeaderHeight();
    } else if (newscrollTop < this.lastScrollTop) {
      document.body.classList.add("scrolled-up");
      document.body.classList.remove("scrolled-down");
      this.setHeaderHeight();
    }
    this.lastScrollTop = newscrollTop <= 0 ? 0 : newscrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add(
      "shopify-section-header-hidden",
      "shopify-section-header-sticky"
    );
    this.closeMenuDisclosure();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add("shopify-section-header-sticky", "animate");
    this.header.classList.remove("shopify-section-header-hidden");
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove(
      "shopify-section-header-hidden",
      "shopify-section-header-sticky",
      "animate"
    );
  }

  closeMenuDisclosure() {
    this.details = this.details || this.header.querySelectorAll("details");
    this.details.forEach((detail) => detail.removeAttribute("open"));
  }
}

const showButton = document.getElementById("showDialog");
const favDialog = document.getElementById("header__search-modal");

if (favDialog) {
  const confirmBtn = favDialog.querySelector("#confirmBtn");

  // "Show the dialog" button opens the <dialog> modally
  showButton.addEventListener("click", () => {
    favDialog.showModal();
  });

  // If the user clicks while the dialog is open
  // If they click outside of the form area, close the modal
  // If they click on the submit button, leave the modal open
  favDialog?.addEventListener("click", (e) => {
    if (e.target === favDialog) {
      favDialog.close();
    }
  });

  // Prevent the "confirm" button from the default behavior of submitting the form, and close the dialog with the `close()` method, which triggers the "close" event.
  confirmBtn?.addEventListener("click", (event) => {
    event.preventDefault(); // We don't want to submit this fake form
  });
}

customElements.define("sticky-header", StickyHeader);

//Buddha Megamenu doesn't work on focus, only opens on hover

// document.addEventListener("DOMContentLoaded", () => {
//   //wait 1 s

//   setTimeout(() => {
//     const menuItems = document.querySelectorAll(".buddha-menu-item");

//     menuItems.forEach((item) => {
//       //add tabindex to menu items
//       item.setAttribute("tabindex", "0");

//       item.addEventListener("focus", () => {
//         //set z index
//         item.style.zIndex = "1001";
//         // item.querySelector(".buddha-mega-menu").open = true;
//         item.classList.add("mega-hover");

//         // get all li within the item

//         const subMenuItems = item.querySelectorAll("li");

//         subMenuItems.forEach((subItem) => {
//           const megaMenuImageContainer = subItem.querySelector(
//             ".mm-image-container"
//           );

//           //get a within megamenuImageContainer
//           if (megaMenuImageContainer) {
//             const megaMenuImageLink = megaMenuImageContainer.querySelector("a");
//             megaMenuImageLink.setAttribute("tabindex", "-1");
//           }

//           //get mm-featured-title

//           const megaMenuFeaturedTitle =
//             subItem.querySelector(".mm-featured-title");

//           megaMenuFeaturedTitle.setAttribute("tabindex", "0");
//           megaMenuFeaturedTitle.addEventListener("focus", () => {
//             megaMenuFeaturedTitle.style.border = "1px solid #000";
//           });
//         });
//       });
//     });
//   }, 1000);
// });

// accordions for mobile menu

document.addEventListener("DOMContentLoaded", () => {
  const drawerAccordions = document.querySelectorAll(
    ".js-drawer-accordion-header"
  );
  if (drawerAccordions.length === 0) return;

  drawerAccordions.forEach((accordion) => {
    accordion.addEventListener("click", () => {
      const content = accordion.nextElementSibling;
      content.classList.toggle("open");
      accordion.classList.toggle("open");
    });
  });
});
